<script lang="ts" setup>
import dayjs from 'dayjs';
import type { StaticPageListItemDetails, StaticPagesListItem } from '~/types/StaticPageList';

const {
  item,
  relatedItems,
  noBreadcrumbs = false,
  relatedItemsVariant = 'default',
} = defineProps<{
  item: StaticPageListItemDetails;
  relatedItems?: StaticPagesListItem[];
  noBreadcrumbs?: boolean;
  relatedItemsVariant?: 'default' | 'blog';
}>();
defineEmits(['load-more']);

const computedImageAlt = computed(() => {
  return item.mainImage?.properties?.alt || item.mainImage?.properties?.title || 'Главный баннер страницы';
});

const formattedDate = dayjs(item.publicationDate).format('DD MMMM YYYY');
</script>

<template>
  <div class="static-page">
    <section
      v-if="!noBreadcrumbs"
      class="static-page__breadcrumbs"
    >
      <CatalogBreadcrumbsBlock
        v-if="item.breadcrumbs"
        :breadcrumbs="item.breadcrumbs"
      />

      <BreadcrumbsBlock v-else />
    </section>

    <section class="static-page__content">
      <div class="w-full">
        <div class="flex justify-between">
          <h1 class="break-words">{{ item.title }}</h1>

          <div
            v-if="item.published_till"
            class="bg-white rounded-lg p-2 shadow-md"
          >
            <CountdownTimer :date="item.published_till" />
          </div>
        </div>

        <p
          v-if="item.publicationDate"
          class="text-xs text-black-60 mt-1"
        >
          {{ formattedDate }}
        </p>
      </div>

      <picture
        v-if="item.isShowMainImage && item.mainImage"
        class="w-full"
      >
        <source
          :srcset="`${item.mainImage.conversions_webp.thumb_390}, ${item.mainImage?.conversions_webp.thumb_780} 2x`"
          media="(max-width: 640px)"
          type="image/webp"
        />

        <source
          :srcset="`${item.mainImage.conversions_webp.thumb_780}, ${item.mainImage?.conversions_webp.thumb_1560} 2x`"
          type="image/webp"
        />

        <source
          :srcset="item.mainImage.conversions.thumb_390"
          type="image/jpeg"
        />

        <img
          :alt="computedImageAlt"
          :src="item.mainImage.url"
          class="static-page__main-image"
          loading="lazy"
        />
      </picture>

      <p
        v-if="item.annotation && item.isAnnotationVisible"
        class="static-page__annotation"
      >
        {{ item.annotation }}
      </p>
    </section>

    <StaticPageConstructorBlocks :blocks="item.constructorBlocks" />

    <StaticPageCardGallery
      v-if="relatedItems && relatedItems.length"
      :items="relatedItems"
      :variant="relatedItemsVariant"
      title="Читайте также"
      @load-more="$emit('load-more')"
    />
  </div>
</template>

<style lang="scss" scoped>
.static-page {
  @include inner-content;

  gap: 16px;
  max-width: $static-content-max-width;

  &__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;

    @include screen-md {
      gap: 32px;
    }
  }

  &__main-image {
    object-fit: contain;
    width: 100%;
    height: auto;
    border-radius: 16px;
    background-color: $white;
    aspect-ratio: 16 / 9;
  }

  &__products-sliders {
    @include screen-sm {
      gap: 24px;
    }
  }
}
</style>
